package com.schoolconnect.app.feature.home

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.ui.draw.clip
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSectionLabel
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.feature.common.shellAvatarInitials
import com.schoolconnect.app.feature.common.shellStaffAuthRoleLabel
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.repository.profile.ProfileRepository
import com.schoolconnect.app.util.decodeImageBitmap
import io.github.vinceglb.filekit.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.core.PickerType
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import kotlinx.coroutines.yield
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.bell
import ischoolconnect.sharedui.generated.resources.camera
import ischoolconnect.sharedui.generated.resources.health_email
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.lock
import org.jetbrains.compose.resources.painterResource

private val EditProfilePrimary = Color(0xFF1F5BFF)
private val EditProfileNavy = Color(0xFF071052)
private val EditProfileMuted = Color(0xFF5A678D)
private val EditProfileBorder = Color(0xFFE5E7EB)
private val EditProfileInputBorder = Color(0xFFE5E7EB)
private val EditProfileAvatarBg = Color(0xFFE3EDFF)
private val EditProfileCardBg = Color(0xFFF8FAFF)
private val EditProfileNoteBg = Color(0xFFEAF2FF)
private val EditProfileNoteBorder = Color(0xFFC9D8FF)
private val EditProfileNoteText = Color(0xFF1A3A9C)
private val EditProfileToggleOn = Color(0xFF2F9E5A)
private val EditProfileDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors


data class EditProfileFormValues(
    val mobileNumber: String = "",
    val email: String = "",
    val fullName: String = "",
    val firstName: String = "",
    val middleName: String = "",
    val lastName: String = "",
    val staffId: String = "",
    val schoolRole: String = "",
    val authRole: String = "",
    val mobileVerified: Boolean? = null,
    val emailVerified: Boolean? = null,
    val pushNotificationsEnabled: Boolean = true,
    val emailNotificationsEnabled: Boolean = true,
    val selectedPhotoName: String = "",
    val selectedPhotoBytes: ByteArray? = null,
    val selectedPhotoFileId: String? = null,
    val profileImageUrl: String = ""
)

internal fun HomeUserProfileUiState.toEditProfileFormValues(): EditProfileFormValues {
    val resolvedFullName = fullName.trim().ifBlank {
        listOf(firstName, middleName, lastName)
            .map(String::trim)
            .filter(String::isNotBlank)
            .joinToString(" ")
    }
    return EditProfileFormValues(
        mobileNumber = mobileNumber,
        email = email,
        fullName = resolvedFullName,
        firstName = firstName,
        middleName = middleName,
        lastName = lastName,
        staffId = employeeId,
        schoolRole = schoolRole,
        authRole = authRole,
        mobileVerified = mobileVerified,
        emailVerified = emailVerified,
        pushNotificationsEnabled = pushNotificationsEnabled,
        emailNotificationsEnabled = emailNotificationsEnabled,
        profileImageUrl = profileImageUrl
    )
}

private fun showsSchoolStaffProfileFields(roleCode: String): Boolean =
    roleCode == SchoolKeys.V_ROLE_SCHOOL_ADMIN || roleCode == SchoolKeys.V_ROLE_TEACHER

private fun isAppItRole(roleCode: String): Boolean =
    roleCode == SchoolKeys.V_ROLE_APP_ADMIN ||
        roleCode == SchoolKeys.V_ROLE_APP_BACKEND ||
        roleCode == SchoolKeys.V_ROLE_APP_SUPER_ADMIN

@Composable
internal fun EditProfileModal(
    open: Boolean,
    initialValues: EditProfileFormValues = EditProfileFormValues(),
    roleCode: String = "",
    onClose: () -> Unit,
    onSave: (EditProfileFormValues) -> Unit,
    onUploadPhoto: suspend (filename: String, contentType: String, bytes: ByteArray) -> Result<String> =
        { filename, contentType, bytes -> ProfileRepository.uploadCurrentProfilePhoto(filename, contentType, bytes) }
) {
    if (!open) return

    val showStaffFields = showsSchoolStaffProfileFields(roleCode)
    val showContactNote = !isAppItRole(roleCode)
    var mobileNumber by remember(initialValues) { mutableStateOf(initialValues.mobileNumber) }
    var email by remember(initialValues) { mutableStateOf(initialValues.email) }
    var fullName by remember(initialValues) { mutableStateOf(initialValues.fullName) }
    var staffId by remember(initialValues) { mutableStateOf(initialValues.staffId) }
    var schoolRole by remember(initialValues) { mutableStateOf(initialValues.schoolRole) }
    var authRole by remember(initialValues) { mutableStateOf(initialValues.authRole) }
    var mobileVerified by remember(initialValues) { mutableStateOf(initialValues.mobileVerified) }
    var emailVerified by remember(initialValues) { mutableStateOf(initialValues.emailVerified) }
    var pushNotificationsEnabled by remember(initialValues) { mutableStateOf(initialValues.pushNotificationsEnabled) }
    var emailNotificationsEnabled by remember(initialValues) { mutableStateOf(initialValues.emailNotificationsEnabled) }
    var selectedPhotoName by remember(initialValues) { mutableStateOf(initialValues.selectedPhotoName) }
    var selectedPhotoBytes by remember(initialValues) { mutableStateOf(initialValues.selectedPhotoBytes) }
    var selectedPhotoFileId by remember(initialValues) { mutableStateOf(initialValues.selectedPhotoFileId) }
    var isPhotoUploading by remember(initialValues) { mutableStateOf(false) }
    var photoUploadError by remember(initialValues) { mutableStateOf<String?>(null) }
    var existingPhotoBytes by remember(initialValues) { mutableStateOf<ByteArray?>(null) }
    val pickerScope = rememberCoroutineScope()
    val saveScope = rememberCoroutineScope()
    val photoPicker = rememberFilePickerLauncher(
        type = PickerType.File(listOf("jpg", "jpeg", "png", "webp")),
        title = "Select Profile Photo"
    ) { file ->
        file ?: return@rememberFilePickerLauncher
        pickerScope.launch {
            runCatching { file.readBytes() }.onSuccess { bytes ->
                if (bytes.size > 2 * 1024 * 1024) {
                    photoUploadError = "Profile photo must be 2MB or smaller."
                    return@onSuccess
                }
                val contentType = when (file.name.substringAfterLast('.', "").lowercase()) {
                    "png" -> "image/png"
                    "webp" -> "image/webp"
                    else -> "image/jpeg"
                }
                selectedPhotoName = file.name
                selectedPhotoBytes = bytes
                selectedPhotoFileId = null
                photoUploadError = null
                isPhotoUploading = true
                onUploadPhoto(file.name, contentType, bytes)
                    .onSuccess { selectedPhotoFileId = it }
                    .onFailure { photoUploadError = it.message ?: "Profile photo upload failed." }
                isPhotoUploading = false
            }.onFailure {
                photoUploadError = it.message ?: "Unable to read the selected photo."
            }
        }
    }

    LaunchedEffect(open, initialValues) {
        if (open) {
            mobileNumber = initialValues.mobileNumber
            email = initialValues.email
            fullName = initialValues.fullName
            staffId = initialValues.staffId
            schoolRole = initialValues.schoolRole
            authRole = initialValues.authRole
            mobileVerified = initialValues.mobileVerified
            emailVerified = initialValues.emailVerified
            pushNotificationsEnabled = initialValues.pushNotificationsEnabled
            emailNotificationsEnabled = initialValues.emailNotificationsEnabled
            selectedPhotoName = initialValues.selectedPhotoName
            selectedPhotoBytes = initialValues.selectedPhotoBytes
            selectedPhotoFileId = initialValues.selectedPhotoFileId
            isPhotoUploading = false
            photoUploadError = null
            existingPhotoBytes = null
            initialValues.profileImageUrl.trim()
                .takeIf { it.isNotBlank() }
                ?.let { url ->
                    FileUploadRepository.downloadBytes(url)
                        .onSuccess { bytes -> existingPhotoBytes = bytes }
                }
        }
    }

    AppDialogScaffold(
        onDismiss = onClose,
        dismissOnClickOutside = false,
        size = AppDialogSize.Wide,
        dialogWidthOverride = 1040.dp,
        colors = EditProfileDialogColors,
        maxDialogHeight = 720.dp,
        isLoading = isPhotoUploading,
        loadingTitle = "Uploading profile photo"
    ) {
        AppDialogHeader(
            title = "My Profile",
            subtitle = "View your profile details and manage preferences.",
            onClose = onClose,
            icon = null,
            closeContentDescription = "Close profile dialog",
            colors = EditProfileDialogColors,
            height = 72.dp
        )
        HorizontalDivider(color = EditProfileBorder)

        AppDialogBody(
            compactBreakpoint = 760.dp,
            colors = EditProfileDialogColors,
            horizontalPadding = 20.dp,
            compactHorizontalPadding = 16.dp,
            verticalPadding = 12.dp
        ) { compact ->
            Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                EditProfilePhotoSection(
                    compact = compact,
                    selectedPhotoBytes = selectedPhotoBytes ?: existingPhotoBytes,
                    initials = shellAvatarInitials(fullName),
                    isPhotoUploading = isPhotoUploading,
                    photoUploadError = photoUploadError,
                    onChangePhoto = { photoPicker.launch() },
                    modifier = Modifier.fillMaxWidth()
                )

                HorizontalDivider(color = EditProfileBorder)

                AppDialogSectionLabel(
                    text = "Profile Information",
                    colors = EditProfileDialogColors
                )

                EditProfileInformationGrid(
                    compact = compact,
                    fullName = fullName,
                    mobileNumber = mobileNumber,
                    email = email,
                    staffId = staffId,
                    schoolRole = schoolRole,
                    authRole = authRole,
                    showStaffFields = showStaffFields
                )

                if (showContactNote) {
                    EditProfileContactNote()
                }

                EditProfileNotificationCard(
                    pushNotificationsEnabled = pushNotificationsEnabled,
                    emailNotificationsEnabled = emailNotificationsEnabled,
                    onPushNotificationsChange = { pushNotificationsEnabled = it },
                    onEmailNotificationsChange = { emailNotificationsEnabled = it }
                )
            }
        }

        HorizontalDivider(color = EditProfileBorder)
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction(
                    label = "Cancel",
                    onClick = onClose,
                    width = 164.dp
                ),
                AppDialogFooterAction(
                    label = "Save",
                    onClick = {
                        val values = EditProfileFormValues(
                            mobileNumber = mobileNumber,
                            email = email,
                            fullName = fullName,
                            staffId = staffId,
                            schoolRole = schoolRole,
                            authRole = authRole,
                            mobileVerified = mobileVerified,
                            emailVerified = emailVerified,
                            pushNotificationsEnabled = pushNotificationsEnabled,
                            emailNotificationsEnabled = emailNotificationsEnabled,
                            selectedPhotoName = selectedPhotoName,
                            selectedPhotoFileId = selectedPhotoFileId
                        )
                        // Let the Compose button finish its pointer/semantics
                        // interaction before the caller closes this dialog.
                        saveScope.launch {
                            yield()
                            // The web click-sound modifier completes after the
                            // callback returns. Keep the dialog attached long
                            // enough for the pointer interaction to settle so
                            // Compose cannot read a detached node.
                            delay(500)
                            onSave(values)
                        }
                    },
                    style = AppDialogActionStyle.Primary,
                    width = 168.dp,
                    enabled = !isPhotoUploading && photoUploadError == null
                )
            ),
            colors = EditProfileDialogColors,
            contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 28.dp)
        )
    }
}

@Composable
private fun EditProfilePhotoSection(
    compact: Boolean,
    selectedPhotoBytes: ByteArray?,
    initials: String,
    isPhotoUploading: Boolean,
    photoUploadError: String?,
    onChangePhoto: () -> Unit,
    modifier: Modifier = Modifier
) {
    val avatarSize = if (compact) 96.dp else 104.dp
    val buttonSize = if (compact) 36.dp else 38.dp

    if (compact) {
        Column(
            modifier = modifier,
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            EditProfileAvatar(
                selectedPhotoBytes = selectedPhotoBytes,
                initials = initials,
                onChangePhoto = onChangePhoto,
                avatarSize = avatarSize,
                buttonSize = buttonSize
            )
            EditProfilePhotoCopy(onChangePhoto = onChangePhoto)
            EditProfilePhotoUploadStatus(isPhotoUploading, photoUploadError)
        }
    } else {
        Row(
            modifier = modifier,
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            EditProfileAvatar(
                selectedPhotoBytes = selectedPhotoBytes,
                initials = initials,
                onChangePhoto = onChangePhoto,
                avatarSize = avatarSize,
                buttonSize = buttonSize
            )
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                EditProfilePhotoCopy(onChangePhoto = onChangePhoto)
                EditProfilePhotoUploadStatus(isPhotoUploading, photoUploadError)
            }
        }
    }
}

@Composable
private fun EditProfileInformationGrid(
    compact: Boolean,
    fullName: String,
    mobileNumber: String,
    email: String,
    staffId: String,
    schoolRole: String,
    authRole: String,
    showStaffFields: Boolean
) {
    val systemRole = shellStaffAuthRoleLabel(authRole)
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            EditProfileLockedField(label = "Name", value = fullName)
            EditProfileLockedField(label = "Mobile Number", value = mobileNumber)
            EditProfileLockedField(label = "Email Address", value = email)
            if (showStaffFields) {
                EditProfileLockedField(label = "Staff ID", value = staffId)
                EditProfileLockedField(label = "Staff Role", value = schoolRole)
                EditProfileLockedField(label = "System Role", value = systemRole)
            }
        }
        return
    }

    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            EditProfileLockedField(label = "Name", value = fullName, modifier = Modifier.weight(1f))
            EditProfileLockedField(label = "Mobile Number", value = mobileNumber, modifier = Modifier.weight(1f))
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            EditProfileLockedField(label = "Email Address", value = email, modifier = Modifier.weight(1f))
            if (showStaffFields) {
                EditProfileLockedField(label = "Staff ID", value = staffId, modifier = Modifier.weight(1f))
            } else {
                Spacer(modifier = Modifier.weight(1f))
            }
        }
        if (showStaffFields) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                EditProfileLockedField(label = "Staff Role", value = schoolRole, modifier = Modifier.weight(1f))
                EditProfileLockedField(label = "System Role", value = systemRole, modifier = Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun EditProfileContactNote() {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(10.dp),
        color = EditProfileNoteBg,
        border = BorderStroke(1.dp, EditProfileNoteBorder)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 12.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                painter = painterResource(Res.drawable.info_circle),
                contentDescription = null,
                tint = EditProfilePrimary,
                modifier = Modifier.size(20.dp)
            )
            Text(
                text = "Please contact your School Administrator to update any profile information.",
                color = EditProfileNoteText,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold
            )
        }
    }
}

@Composable
private fun EditProfilePhotoUploadStatus(isUploading: Boolean, error: String?) {
    when {
        isUploading -> Text(
            text = "Uploading photo…",
            color = EditProfileMuted,
            fontSize = 12.sp
        )
        error != null -> Text(
            text = error,
            color = Color(0xFFD92D20),
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold
        )
    }
}

@Composable
private fun EditProfileAvatar(
    selectedPhotoBytes: ByteArray?,
    initials: String,
    onChangePhoto: () -> Unit,
    avatarSize: androidx.compose.ui.unit.Dp,
    buttonSize: androidx.compose.ui.unit.Dp
) {
    Box(modifier = Modifier.size(avatarSize)) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(EditProfileAvatarBg, CircleShape),
            contentAlignment = Alignment.Center
        ) {
            val preview = remember(selectedPhotoBytes) { selectedPhotoBytes?.let(::decodeImageBitmap) }
            if (preview != null) {
                androidx.compose.foundation.Image(
                    bitmap = preview,
                    contentDescription = "Selected profile photo",
                    contentScale = androidx.compose.ui.layout.ContentScale.Crop,
                    modifier = Modifier.fillMaxSize().clip(CircleShape)
                )
            } else {
                Text(
                    text = initials.ifBlank { "SC" },
                    color = EditProfilePrimary,
                    fontSize = (avatarSize.value * 0.32f).sp,
                    fontWeight = FontWeight.ExtraBold
                )
            }
        }

        Box(
            modifier = Modifier
                .align(Alignment.BottomEnd)
                .size(buttonSize)
                .background(EditProfilePrimary, CircleShape)
                .border(3.dp, Color.White, CircleShape)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onChangePhoto
                ),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = painterResource(Res.drawable.camera),
                contentDescription = "Change photo",
                tint = Color.White,
                modifier = Modifier.size(buttonSize * 0.46f)
            )
        }
    }
}

@Composable
private fun EditProfilePhotoCopy(
    onChangePhoto: () -> Unit
) {
    Column(
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = "Profile Photo",
            color = EditProfileNavy,
            fontSize = 18.sp,
            fontWeight = FontWeight.ExtraBold
        )
        Text(
            text = "JPG, PNG or WEBP. Max size 2MB.",
            color = EditProfileMuted,
            fontSize = 14.sp,
            fontWeight = FontWeight.Medium
        )
        TextButton(
            onClick = onChangePhoto,
            contentPadding = androidx.compose.foundation.layout.PaddingValues(0.dp)
        ) {
            Text(
                text = "Change Photo",
                color = EditProfilePrimary,
                fontSize = 16.sp,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun EditProfileLockedField(
    label: String,
    value: String,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(7.dp)) {
        AppDialogFieldColumn(label = label, colors = EditProfileDialogColors) {
            Surface(
                modifier = Modifier.fillMaxWidth().height(44.dp),
                shape = RoundedCornerShape(8.dp),
                color = EditProfileCardBg,
                border = BorderStroke(1.dp, EditProfileInputBorder)
            ) {
                Row(
                    modifier = Modifier.fillMaxSize().padding(horizontal = 12.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = value.ifBlank { "—" },
                        color = EditProfileNavy.copy(alpha = 0.72f),
                        fontSize = 15.sp,
                        fontWeight = FontWeight.Medium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    Icon(
                        painter = painterResource(Res.drawable.lock),
                        contentDescription = "Read only",
                        tint = EditProfileMuted,
                        modifier = Modifier.size(18.dp)
                    )
                }
            }
        }
    }
}

@Composable
private fun EditProfileNotificationCard(
    pushNotificationsEnabled: Boolean,
    emailNotificationsEnabled: Boolean,
    onPushNotificationsChange: (Boolean) -> Unit,
    onEmailNotificationsChange: (Boolean) -> Unit
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        color = EditProfileCardBg,
        border = BorderStroke(1.dp, EditProfileBorder)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(
                text = "Notification Preferences",
                color = EditProfileNavy,
                fontSize = 16.sp,
                fontWeight = FontWeight.ExtraBold
            )

            EditProfileNotificationRow(
                iconPainter = painterResource(Res.drawable.bell),
                title = "Push Notifications",
                subtitle = "Receive notifications on your device",
                checked = pushNotificationsEnabled,
                onCheckedChange = onPushNotificationsChange
            )

            HorizontalDivider(color = EditProfileBorder)

            EditProfileNotificationRow(
                iconPainter = painterResource(Res.drawable.health_email),
                title = "Email Notifications",
                subtitle = "Receive notifications via email",
                checked = emailNotificationsEnabled,
                onCheckedChange = onEmailNotificationsChange
            )
        }
    }
}

@Composable
private fun EditProfileNotificationRow(
    iconPainter: androidx.compose.ui.graphics.painter.Painter,
    title: String,
    subtitle: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit
) {
    val statusLabel = if (checked) "On" else "Off"
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            modifier = Modifier.weight(1f),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .background(EditProfileAvatarBg, RoundedCornerShape(10.dp)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = iconPainter,
                    contentDescription = null,
                    tint = EditProfilePrimary,
                    modifier = Modifier.size(18.dp)
                )
            }
            Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    text = title,
                    color = EditProfileNavy,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.ExtraBold
                )
                Text(
                    text = subtitle,
                    color = EditProfileMuted,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium
                )
            }
        }

        Text(
            text = statusLabel,
            color = if (checked) EditProfileToggleOn else EditProfileMuted,
            fontSize = 14.sp,
            fontWeight = FontWeight.SemiBold
        )
        Switch(
            checked = checked,
            onCheckedChange = onCheckedChange,
            colors = SwitchDefaults.colors(
                checkedThumbColor = Color.White,
                checkedTrackColor = EditProfilePrimary,
                checkedBorderColor = EditProfilePrimary,
                uncheckedThumbColor = Color.White,
                uncheckedTrackColor = Color(0xFFD5DEF4),
                uncheckedBorderColor = Color(0xFFD5DEF4)
            )
        )
    }
}
